import _ from 'lodash';
import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RouteReuseStrategyService } from 'app/router-wrapper/services/route-reuse-strategy-service/route-reuse-strategy-service.service';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { ADTSettings  } from 'angular-datatables/src/models/settings';
import 'datatables.net-buttons-dt';
import { UserListService } from 'app/users/services/user-list/user-list.service';
import { UserFormModalService } from 'app/users/services/user-form-modal/user-form-modal.service';
import { UserActionCellComponent } from '../user-action-cell/user-action-cell.component';
import { UserService } from 'app/users/services/user/user.service';
import { DataTableDirective } from 'angular-datatables';
import { ToastService } from 'app/toast/services/toast.service';
import { UserRoleCellComponent } from '../user-role-cell/user-role-cell.component';
import { UserStatusCellComponent } from '../user-status-cell/user-status-cell.component';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UsersListComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostListener('click', ['$event'])
  onClick(event) {
    const isAddNewAccount = this.isDelegateElement(event, 'addNewAccount');
    if(isAddNewAccount) {
      return this.addUser();
    }
  }

  @Input() readonly: boolean = false;

  private takeUntilSubject: Subject<any> = new Subject();
  private takeUntilRouteReuseStrategySubject: Subject<any> = new Subject();

  @ViewChild(DataTableDirective, {static: false})
  public dtElement: DataTableDirective;

  public dtOptions: ADTSettings = {};
  public dtTrigger: Subject<ADTSettings> = new Subject<ADTSettings>();

  @ViewChild('userRoleCell') userRoleCell: TemplateRef<UserRoleCellComponent>;
  @ViewChild('userStatusCell') userStatusCell: TemplateRef<UserStatusCellComponent>;
  @ViewChild('userActionCell') userActionCell: TemplateRef<UserActionCellComponent>;
  
  constructor(
    private routeReuseStrategyService: RouteReuseStrategyService,
    private userService: UserService,
    private userListService: UserListService,
    private userFormModalService: UserFormModalService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.routeReuseStrategyService.status = true;
    this.routeReuseStrategyService.routeReuseStrategySubject.pipe(
      takeUntil(this.takeUntilRouteReuseStrategySubject)
    ).subscribe(() => {
      this.takeUntilSubject.next(null);
      this.takeUntilSubject.complete();
      this.takeUntilSubject = new Subject();

      this.ngAfterViewInit();
    });
  }

  ngAfterViewInit(): void {
    this.dtOptions = {
      ajax: (dataTablesParameters: any, callback) => {
        return this.userListService.getUserList().subscribe((response: any[]) => {
          callback({
            recordsTotal: response.length,
            data: response
          });
        });
      },
      dom: "<'table__header--paybee d-flex align-items-center justify-content-between pb-3 pt-3'<''B><''f>><'table-responsive't><'table__footer--paybee d-flex align-items-center justify-content-between p-4'ip>",
      buttons: [{
          className: 'btn-copy-code btn btn-outline-grey btn-sm px-2 waves-effect',
          extend: 'excel',
          filename: 'adminList',
          text: "<i class='fas fa-cloud-download-alt cursor--pointer mr-1'></i><span class='cursor--pointer'>Export</span>",
          title: null,
          exportOptions: {
              stripHtml: true,
              columns: "thead th:not(.no-export)"
          }
      }],
      columns: [
        {
            "data": "name",
            "width": "20%"
        },
        {
            "data": "email",
            "width": "20%"
        },
        {
          "data": null,
          "width": "15%",
          "defaultContent": '',
          "ngTemplateRef": {
            "ref": this.userRoleCell
          }
        },
        {
          "data": null,
          "width": "15%",
          "defaultContent": '',
          "ngTemplateRef": {
            "ref": this.userStatusCell
          }
        },
      ],
      destroy: true
    }

    if(!this.readonly) {
      this.dtOptions.columns.push({
        "data": null,
        "width": "15%",
        "defaultContent": '',
        "ngTemplateRef": {
          "ref": this.userActionCell,
          "context": {
              // needed for capturing events inside <ng-template>
              captureEvents: this.onCaptureEvent.bind(this)
          }
        }
      })
    }

    setTimeout(() => {
      // race condition fails unit tests if dtOptions isn't sent with dtTrigger
      this.dtTrigger.next(this.dtOptions);
    }, 200);
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.takeUntilRouteReuseStrategySubject.next(null);
    this.takeUntilRouteReuseStrategySubject.complete();

    this.routeReuseStrategyService.status = false;
  }

  addUser() {
    const modalRef = this.userFormModalService.open(null);
    return modalRef.onClose.pipe(
      first()
    ).subscribe(message => {
      if(message) {
        this.dtElement.dtInstance.then(dtInstance => {
          // Destroy the table first
          dtInstance.destroy();
          // Call the dtTrigger to rerender again
          this.dtTrigger.next(null);
        });
      }
    });
  }

  onCaptureEvent(event) {
    const { data: { id } } = event;

    switch(event.cmd) {
      case "edit": {
        const modalRef = this.userFormModalService.open(event.data);
        return modalRef.onClose.pipe(
          first()
        ).subscribe(message => {
          if(message) {
            this.dtElement.dtInstance.then(dtInstance => {
              // Destroy the table first
              dtInstance.destroy();
              // Call the dtTrigger to rerender again
              this.dtTrigger.next(null);
            });
          }
        });
      }
      case "delete": {
        return this.userService.delete(id).pipe(
          takeUntil(this.takeUntilSubject)
        ).subscribe(() => {
          this.toastService.showSuccess("User Deleted");
          this.dtElement.dtInstance.then(dtInstance => {
            // Destroy the table first
            dtInstance.destroy();
            // Call the dtTrigger to rerender again
            this.dtTrigger.next(null);
          });
        });
      }
      case "reset-password": {
        return this.userService.resetPassword(id).pipe(
          takeUntil(this.takeUntilSubject)
        ).subscribe(() => {
          this.toastService.showSuccess("Sent email to with instructions for resetting password.");
          this.dtElement.dtInstance.then(dtInstance => {
            // Destroy the table first
            dtInstance.destroy();
            // Call the dtTrigger to rerender again
            this.dtTrigger.next(null);
          });
        });
      }
    }
  }

  isDelegateElement(event, id) {
    let target: HTMLElement = event.target;
    while(target) {
      if(target.id === id) {
        return true;
      }
      target = target.parentElement;
    }
    return false;
  }
}
